import { Tooltip } from 'antd'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'

const EllipsisTooltip = ({
  children,
  selector,
  className = '',
  width = '100%',
  value,
  lineClamp = null,
}) => {
  const wrapperRef = useRef(null)
  const [showTooltip, setShowTooltip] = useState(false)

  /**
   * If selector is supplied, find the target inside the wrapper.
   * Otherwise, the wrapper itself is the target because the
   * ellipsis styles are applied to the wrapper.
   */
  const getTarget = useCallback(() => {
    const wrapper = wrapperRef.current

    if (!wrapper) return null

    if (selector) {
      return wrapper.querySelector(selector)
    }

    return wrapper
  }, [selector])

  /**
   * Measure text inside an input.
   *
   * scrollWidth is not reliable for disabled/readOnly inputs,
   * so we measure the text using a canvas.
   */
  const isInputOverflow = useCallback((input) => {
    if (!input) return false

    const text = value ?? input.value ?? ''

    if (!text) return false

    const style = window.getComputedStyle(input)

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) return false

    ctx.font = [
      style.fontStyle,
      style.fontVariant,
      style.fontWeight,
      style.fontSize,
      style.fontFamily,
    ]
      .filter(Boolean)
      .join(' ')

    const textWidth = ctx.measureText(text).width

    const availableWidth =
      input.clientWidth -
      parseFloat(style.paddingLeft || 0) -
      parseFloat(style.paddingRight || 0)

    return textWidth > availableWidth
  }, [value])

  const checkOverflow = useCallback(() => {
    const element = getTarget()

    if (!element) {
      setShowTooltip(false)
      return
    }

    let overflow = false

    if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
      overflow = isInputOverflow(element)
    } else if (lineClamp) {
      // Multiline ellipsis
      overflow = element.scrollHeight > element.clientHeight + 1
    } else {
      // Normal single-line ellipsis
      overflow = element.scrollWidth > element.clientWidth + 1
    }

    setShowTooltip(overflow)
  }, [getTarget, isInputOverflow, lineClamp])

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current

    if (!wrapper) return

    /*
     * Run after the browser has rendered the latest value/content.
     * This is particularly useful when the value comes from state/API.
     */
    const frame = requestAnimationFrame(checkOverflow)

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(checkOverflow)
    })

    resizeObserver.observe(wrapper)

    window.addEventListener('resize', checkOverflow)

    return () => {
      cancelAnimationFrame(frame)
      resizeObserver.disconnect()
      window.removeEventListener('resize', checkOverflow)
    }
  }, [checkOverflow, value, children])

  return (
    <Tooltip
      title={showTooltip ? (value ?? children) : null}
      open={showTooltip ? undefined : false}
      zIndex={1001}
    >
      <span
        ref={wrapperRef}
        className={className}
        style={{
          display: lineClamp ? '-webkit-box' : 'block',
          width,
          minWidth: 0,
          ...(lineClamp
            ? {
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: lineClamp,
                overflow: 'hidden',
                whiteSpace: 'normal',
              }
            : {}),
        }}
      >
        {children}
      </span>
    </Tooltip>
  )
}

export default EllipsisTooltip
